import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../constants/config';
import sseService from '../services/sseService';

/**
 * Security Registry Redux Slice
 * Mirrors pathRegistry patterns with security-specific enhancements
 */

// Helper function to get auth token
const getToken = () => {
  return localStorage.getItem('authToken') || localStorage.getItem('token');
};

// Helper function to create API headers
const createHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${getToken()}`
});

// Security Registry API endpoints
const SECURITY_REGISTRY_BASE = `${config.BASE_URL}/security-registry`;

// Async thunks for security registry operations

/**
 * Refresh security registry from server
 */
export const refreshSecurityRegistry = createAsyncThunk(
  'securityRegistry/refresh',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`${SECURITY_REGISTRY_BASE}/refresh`, {
        method: 'POST',
        headers: createHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error);
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue({ error: error.message });
    }
  }
);

/**
 * Fetch all security definitions
 */
export const fetchSecurityDefinitions = createAsyncThunk(
  'securityRegistry/fetchDefinitions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`${SECURITY_REGISTRY_BASE}/definitions`, {
        headers: createHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error);
      }

      const result = await response.json();
      return result.definitions;
    } catch (error) {
      return rejectWithValue({ error: error.message });
    }
  }
);

/**
 * Fetch specific security definition by name
 */
export const fetchSecurityDefinition = createAsyncThunk(
  'securityRegistry/fetchDefinition',
  async (name, { rejectWithValue }) => {
    try {
      const response = await fetch(`${SECURITY_REGISTRY_BASE}/definitions/${name}`, {
        headers: createHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error);
      }

      const result = await response.json();
      return result.definition;
    } catch (error) {
      return rejectWithValue({ error: error.message });
    }
  }
);

/**
 * Fetch security definitions by entity type
 */
export const fetchDefinitionsByEntityType = createAsyncThunk(
  'securityRegistry/fetchByEntityType',
  async (entityType, { rejectWithValue }) => {
    try {
      const response = await fetch(`${SECURITY_REGISTRY_BASE}/definitions/by-entity/${entityType}`, {
        headers: createHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error);
      }

      const result = await response.json();
      return { entityType, definitions: result.definitions };
    } catch (error) {
      return rejectWithValue({ error: error.message });
    }
  }
);

/**
 * Create new security definition
 */
export const createSecurityDefinition = createAsyncThunk(
  'securityRegistry/create',
  async (definition, { rejectWithValue }) => {
    try {
      const response = await fetch(`${SECURITY_REGISTRY_BASE}/definitions`, {
        method: 'POST',
        headers: createHeaders(),
        body: JSON.stringify(definition)
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error);
      }

      const result = await response.json();
      return result.definition;
    } catch (error) {
      return rejectWithValue({ error: error.message });
    }
  }
);

/**
 * Update existing security definition
 */
export const updateSecurityDefinition = createAsyncThunk(
  'securityRegistry/update',
  async ({ name, definition }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${SECURITY_REGISTRY_BASE}/definitions/${name}`, {
        method: 'PUT',
        headers: createHeaders(),
        body: JSON.stringify(definition)
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error);
      }

      const result = await response.json();
      return result.definition;
    } catch (error) {
      return rejectWithValue({ error: error.message });
    }
  }
);

/**
 * Delete security definition
 */
export const deleteSecurityDefinition = createAsyncThunk(
  'securityRegistry/delete',
  async (name, { rejectWithValue }) => {
    try {
      const response = await fetch(`${SECURITY_REGISTRY_BASE}/definitions/${name}`, {
        method: 'DELETE',
        headers: createHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error);
      }

      const result = await response.json();
      return { name, deletedDefinition: result.deletedDefinition };
    } catch (error) {
      return rejectWithValue({ error: error.message });
    }
  }
);

/**
 * Validate security definition structure (server-only validation)
 */
export const validateSecurityDefinition = createAsyncThunk(
  'securityRegistry/validateDefinition',
  async (definition, { rejectWithValue }) => {
    try {
      const response = await fetch(`${SECURITY_REGISTRY_BASE}/definitions/validate`, {
        method: 'POST',
        headers: createHeaders(),
        body: JSON.stringify(definition)
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error);
      }

      const result = await response.json();
      return result.validation;
    } catch (error) {
      return rejectWithValue({ error: error.message });
    }
  }
);

/**
 * Validate entity data against security definition
 */
export const validateEntitySecurity = createAsyncThunk(
  'securityRegistry/validate',
  async ({ entityData, securityDefinitionName, operation = 'CREATE' }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${SECURITY_REGISTRY_BASE}/validate`, {
        method: 'POST',
        headers: createHeaders(),
        body: JSON.stringify({ entityData, securityDefinitionName, operation })
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error);
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue({ error: error.message });
    }
  }
);

/**
 * Fetch security registry status
 */
export const fetchRegistryStatus = createAsyncThunk(
  'securityRegistry/fetchStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`${SECURITY_REGISTRY_BASE}/status`, {
        headers: createHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error);
      }

      const result = await response.json();
      return result.status;
    } catch (error) {
      return rejectWithValue({ error: error.message });
    }
  }
);

/**
 * Fetch audit trail
 */
export const fetchAuditTrail = createAsyncThunk(
  'securityRegistry/fetchAuditTrail',
  async (limit = 100, { rejectWithValue }) => {
    try {
      const response = await fetch(`${SECURITY_REGISTRY_BASE}/audit-trail?limit=${limit}`, {
        headers: createHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error);
      }

      const result = await response.json();
      return result.auditTrail;
    } catch (error) {
      return rejectWithValue({ error: error.message });
    }
  }
);

// Initial state
const initialState = {
  definitions: [],
  definitionsByEntity: {},
  selectedDefinition: null,
  registryStatus: null,
  auditTrail: [],
  lastRefresh: null,
  isLoading: false,
  isRefreshing: false,
  isValidating: false,
  error: null,
  validationResult: null,
  // SSE connection state
  sseConnectionState: 'disconnected', // disconnected, connecting, connected, error
  sseError: null,
  lastSseEvent: null
};

// Security Registry slice
const securityRegistrySlice = createSlice({
  name: 'securityRegistry',
  initialState,
  reducers: {
    // Clear error state
    clearError: (state) => {
      state.error = null;
    },

    // Clear validation result
    clearValidationResult: (state) => {
      state.validationResult = null;
    },

    // Set selected definition
    setSelectedDefinition: (state, action) => {
      state.selectedDefinition = action.payload;
    },

    // Clear selected definition
    clearSelectedDefinition: (state) => {
      state.selectedDefinition = null;
    },

    // Real-time update from SSE
    registryUpdated: (state, action) => {
      state.definitions = action.payload.definitions;
      state.lastRefresh = action.payload.timestamp;
    },

    // SSE connection state management
    setSseConnectionState: (state, action) => {
      state.sseConnectionState = action.payload;
      if (action.payload === 'connected') {
        state.sseError = null;
      }
    },

    setSseError: (state, action) => {
      state.sseError = action.payload;
      state.sseConnectionState = 'error';
    },

    clearSseError: (state) => {
      state.sseError = null;
    },

    // Handle SSE registry change events
    handleSseRegistryEvent: (state, action) => {
      const { type, definition, definitionName, timestamp } = action.payload;
      state.lastSseEvent = { type, timestamp };

      switch (type) {
        case 'definition-created':
          if (definition && !state.definitions.find(def => def.name === definition.name)) {
            state.definitions.push(definition);
          }
          break;

        case 'definition-updated':
          if (definition) {
            const index = state.definitions.findIndex(def => def.name === definition.name);
            if (index !== -1) {
              state.definitions[index] = definition;
            }
            // Update selected definition if it's the one being updated
            if (state.selectedDefinition?.name === definition.name) {
              state.selectedDefinition = definition;
            }
          }
          break;

        case 'definition-deleted':
          if (definitionName) {
            state.definitions = state.definitions.filter(def => def.name !== definitionName);
            // Clear selected definition if it's the one being deleted
            if (state.selectedDefinition?.name === definitionName) {
              state.selectedDefinition = null;
            }
          }
          break;

        default:
          console.warn('Unknown SSE registry event type:', type);
      }

      state.lastRefresh = timestamp;
    },

    // Reset state
    resetState: () => initialState
  },
  extraReducers: (builder) => {
    builder
      // Refresh security registry
      .addCase(refreshSecurityRegistry.pending, (state) => {
        state.isRefreshing = true;
        state.error = null;
      })
      .addCase(refreshSecurityRegistry.fulfilled, (state, action) => {
        state.isRefreshing = false;
        state.lastRefresh = action.payload.lastRefresh;
      })
      .addCase(refreshSecurityRegistry.rejected, (state, action) => {
        state.isRefreshing = false;
        state.error = action.payload?.error || 'Failed to refresh security registry';
      })

      // Fetch security definitions
      .addCase(fetchSecurityDefinitions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSecurityDefinitions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.definitions = action.payload;
      })
      .addCase(fetchSecurityDefinitions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.error || 'Failed to fetch security definitions';
      })

      // Fetch specific security definition
      .addCase(fetchSecurityDefinition.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSecurityDefinition.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedDefinition = action.payload;
      })
      .addCase(fetchSecurityDefinition.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.error || 'Failed to fetch security definition';
      })

      // Fetch definitions by entity type
      .addCase(fetchDefinitionsByEntityType.fulfilled, (state, action) => {
        const { entityType, definitions } = action.payload;
        state.definitionsByEntity[entityType] = definitions;
      })

      // Create security definition
      .addCase(createSecurityDefinition.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createSecurityDefinition.fulfilled, (state, action) => {
        state.isLoading = false;
        state.definitions.push(action.payload);
        state.selectedDefinition = action.payload;
      })
      .addCase(createSecurityDefinition.rejected, (state, action) => {
        state.isLoading = false;

        // Extract detailed validation errors if available
        const payload = action.payload;
        if (payload?.validationErrors && Array.isArray(payload.validationErrors)) {
          state.error = `Validation failed: ${payload.validationErrors.join(', ')}`;
        } else {
          state.error = payload?.error || 'Failed to create security definition';
        }
      })

      // Update security definition
      .addCase(updateSecurityDefinition.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateSecurityDefinition.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.definitions.findIndex(def => def.name === action.payload.name);
        if (index !== -1) {
          state.definitions[index] = action.payload;
        }
        state.selectedDefinition = action.payload;
      })
      .addCase(updateSecurityDefinition.rejected, (state, action) => {
        state.isLoading = false;

        // Extract detailed validation errors if available
        const payload = action.payload;
        console.log("VALIDATION ERRORS", payload?.validationErrors);
        if (payload?.validationErrors && Array.isArray(payload.validationErrors)) {
          console.log('VALIDATION ERRORS LIST', `Validation failed: ${payload.validationErrors.join(', ')}`)
          state.error = `Validation failed: ${payload.validationErrors.join(', ')}`;
        } else {
          state.error = payload?.error || 'Failed to update security definition';
        }
      })

      // Delete security definition
      .addCase(deleteSecurityDefinition.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteSecurityDefinition.fulfilled, (state, action) => {
        state.isLoading = false;
        state.definitions = state.definitions.filter(def => def.name !== action.payload.name);
        if (state.selectedDefinition?.name === action.payload.name) {
          state.selectedDefinition = null;
        }
      })
      .addCase(deleteSecurityDefinition.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.error || 'Failed to delete security definition';
      })

      // Validate security definition structure
      .addCase(validateSecurityDefinition.pending, (state) => {
        state.isValidating = true;
        state.error = null;
        state.validationResult = null;
      })
      .addCase(validateSecurityDefinition.fulfilled, (state, action) => {
        state.isValidating = false;
        state.validationResult = action.payload;
      })
      .addCase(validateSecurityDefinition.rejected, (state, action) => {
        state.isValidating = false;
        state.error = action.payload?.error || 'Failed to validate security definition';
      })

      // Validate entity security
      .addCase(validateEntitySecurity.pending, (state) => {
        state.isValidating = true;
        state.error = null;
        state.validationResult = null;
      })
      .addCase(validateEntitySecurity.fulfilled, (state, action) => {
        state.isValidating = false;
        state.validationResult = action.payload;
      })
      .addCase(validateEntitySecurity.rejected, (state, action) => {
        state.isValidating = false;
        state.error = action.payload?.error || 'Failed to validate entity security';
      })

      // Fetch registry status
      .addCase(fetchRegistryStatus.fulfilled, (state, action) => {
        state.registryStatus = action.payload;
      })

      // Fetch audit trail
      .addCase(fetchAuditTrail.fulfilled, (state, action) => {
        state.auditTrail = action.payload;
      });
  }
});

// Export actions
export const {
  clearError,
  clearValidationResult,
  setSelectedDefinition,
  clearSelectedDefinition,
  registryUpdated,
  setSseConnectionState,
  setSseError,
  clearSseError,
  handleSseRegistryEvent,
  resetState
} = securityRegistrySlice.actions;

// Selectors
export const selectSecurityDefinitions = (state) => state.securityRegistry.definitions;
export const selectDefinitionsByEntity = (state) => state.securityRegistry.definitionsByEntity;
export const selectSelectedDefinition = (state) => state.securityRegistry.selectedDefinition;
export const selectCurrentSecurityDefinition = (state) => state.securityRegistry.selectedDefinition;
export const selectRegistryStatus = (state) => state.securityRegistry.registryStatus;
export const selectAuditTrail = (state) => state.securityRegistry.auditTrail;
export const selectIsLoading = (state) => state.securityRegistry.isLoading;
export const selectIsRefreshing = (state) => state.securityRegistry.isRefreshing;
export const selectIsValidating = (state) => state.securityRegistry.isValidating;
export const selectError = (state) => state.securityRegistry.error;
export const selectValidationResult = (state) => state.securityRegistry.validationResult;
export const selectLastRefresh = (state) => state.securityRegistry.lastRefresh;

// SSE selectors
export const selectSseConnectionState = (state) => state.securityRegistry.sseConnectionState;
export const selectSseError = (state) => state.securityRegistry.sseError;
export const selectLastSseEvent = (state) => state.securityRegistry.lastSseEvent;
export const selectIsSseConnected = (state) => state.securityRegistry.sseConnectionState === 'connected';

// Helper selectors
export const selectDefinitionByName = (name) => (state) =>
  state.securityRegistry.definitions.find(def => def.name === name);

export const selectDefinitionsByEntityType = (entityType) => (state) =>
  state.securityRegistry.definitionsByEntity[entityType] || [];

export const selectCriticalDefinitions = (state) =>
  state.securityRegistry.definitions.filter(def => def.criticality === 'CRITICAL');

export const selectActiveDefinitions = (state) =>
  state.securityRegistry.definitions.filter(def => def.active);

export default securityRegistrySlice.reducer;
